/**
 * PrizesBlock — Prize showcase with tiered display
 *
 * Three layout modes:
 *   - "grid"     — responsive CSS grid (default fallback)
 *   - "featured" — grand prize hero card spanning full width, rest in grid
 *   - "carousel" — horizontal scroll-snap carousel with arrows + pagination dots
 */
import { useRef, useState, useEffect, useCallback } from "react";
import { SurfacePrizesConfigResponse, SurfacePrizeTier } from "@/hooks/hub/useGetHub";
import "./PrizesBlock.scss";

/** Full-size image lightbox triggered by clicking a prize card */
const PrizeLightbox = ({
    imageUrl,
    title,
    onClose,
}: {
    imageUrl: string;
    title: string;
    onClose: () => void;
}) => {
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [onClose]);

    return (
        <div
            className="prizes-block__lightbox"
            onClick={onClose}
            role="dialog"
            aria-label={`${title} full size`}
        >
            <img
                src={imageUrl}
                alt={title}
                className="prizes-block__lightbox-image"
                onClick={(e) => e.stopPropagation()}
            />
        </div>
    );
};

interface PrizesBlockProps {
    config?: SurfacePrizesConfigResponse;
    sectionTitle?: string;
}

const TIER_LABELS: Record<SurfacePrizeTier, string> = {
    GRAND: "Grand Prize",
    FIRST: "1st Place",
    SECOND: "2nd Place",
    THIRD: "3rd Place",
    PARTICIPATION: "Participation",
};

const TIER_COLORS: Record<SurfacePrizeTier, string> = {
    GRAND: "#FFD700",
    FIRST: "#FFD700",
    SECOND: "#C0C0C0",
    THIRD: "#CD7F32",
    PARTICIPATION: "var(--hub-textSecondary, #9C9C9D)",
};

/** Shared prize card used by all three layouts */
const PrizeCard = ({
    prize,
    showValues,
    className = "",
    onClick,
}: {
    prize: SurfacePrizesConfigResponse["prizes"][number];
    showValues?: boolean;
    className?: string;
    onClick?: () => void;
}) =>
    <div
        className={`prizes-block__card prizes-block__card--${prize.tier.toLowerCase()} ${className} ${prize.imageUrl ? "prizes-block__card--clickable" : ""}`}
        onClick={prize.imageUrl ? onClick : undefined}
    >
        {prize.imageUrl &&
            <img src={prize.imageUrl} alt={prize.title} className="prizes-block__image" />
        }
        <span
            className="prizes-block__tier"
            style={{ color: TIER_COLORS[prize.tier] }}
        >
            {TIER_LABELS[prize.tier]}
        </span>
        <h4 className="prizes-block__name">{prize.title}</h4>
        {prize.description && <p className="prizes-block__desc">{prize.description}</p>}
        {showValues && prize.value &&
            <span className="prizes-block__value">{prize.value}</span>
        }
        {prize.quantity && prize.quantity > 1 &&
            <span className="prizes-block__quantity">{prize.quantity} winners</span>
        }
    </div>;
export const PrizesBlock = ({ config, sectionTitle }: PrizesBlockProps) => {
    const [lightboxImage, setLightboxImage] = useState<{ url: string; title: string } | null>(null);

    if (!config?.prizes?.length) return null;

    const layout = config.layout || "featured";

    if (layout === "carousel") {
        return (
            <>
                <PrizeCarousel config={config} sectionTitle={sectionTitle} onPrizeClick={setLightboxImage} />
                {lightboxImage &&
                    <PrizeLightbox
                        imageUrl={lightboxImage.url}
                        title={lightboxImage.title}
                        onClose={() => setLightboxImage(null)}
                    />
                }
            </>
        );
    }

    // Grid + Featured layouts use the same DOM — featured styling is CSS-driven
    return (
        <div className={`prizes-block prizes-block--${layout}`}>
            {sectionTitle && <h3 className="prizes-block__title">{sectionTitle}</h3>}
            <div className="prizes-block__grid">
                {config.prizes.map((prize, index) =>
                    <PrizeCard
                        key={prize._id || index}
                        prize={prize}
                        showValues={config.showValues}
                        onClick={() => prize.imageUrl && setLightboxImage({ url: prize.imageUrl, title: prize.title })}
                    />)}
            </div>
            {lightboxImage &&
                <PrizeLightbox
                    imageUrl={lightboxImage.url}
                    title={lightboxImage.title}
                    onClose={() => setLightboxImage(null)}
                />
            }
        </div>
    );
};

/** Carousel sub-component — scroll-snap + arrows + dots */
const PrizeCarousel = ({
    config,
    sectionTitle,
    onPrizeClick,
}: {
    config: SurfacePrizesConfigResponse;
    sectionTitle?: string;
    onPrizeClick?: (img: { url: string; title: string }) => void;
}) => {
    const carouselRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const prizes = config.prizes;

    // Track scroll position → active dot
    useEffect(() => {
        const carousel = carouselRef.current;
        if (!carousel) return;

        const handleScroll = () => {
            const scrollLeft = carousel.scrollLeft;
            const cardWidth = carousel.firstElementChild?.clientWidth || 320;
            const gap = 24; // Must match .prizes-block__carousel gap in PrizesBlock.scss
            const index = Math.round(scrollLeft / (cardWidth + gap));
            setActiveIndex(Math.min(index, prizes.length - 1));
        };

        carousel.addEventListener("scroll", handleScroll, { passive: true });
        return () => carousel.removeEventListener("scroll", handleScroll);
    }, [prizes.length]);

    const scrollToIndex = useCallback((index: number) => {
        if (!carouselRef.current) return;
        const cardWidth = carouselRef.current.firstElementChild?.clientWidth || 320;
        const gap = 24;
        carouselRef.current.scrollTo({
            left: index * (cardWidth + gap),
            behavior: "smooth",
        });
    }, []);

    const scrollCarousel = useCallback((direction: "left" | "right") => {
        setActiveIndex(prev => {
            const next = direction === "left"
                ? Math.max(0, prev - 1)
                : Math.min(prizes.length - 1, prev + 1);
            scrollToIndex(next);
            return next;
        });
    }, [prizes.length, scrollToIndex]);

    return (
        <div className="prizes-block prizes-block--carousel">
            {sectionTitle && <h3 className="prizes-block__title">{sectionTitle}</h3>}

            <div className="prizes-block__carousel-wrapper">
                {/* Left Arrow */}
                <button
                    type="button"
                    className="prizes-block__nav prizes-block__nav--left"
                    onClick={() => scrollCarousel("left")}
                    aria-label="Previous prize"
                    disabled={activeIndex === 0}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                {/* Scroll container */}
                <div className="prizes-block__carousel" ref={carouselRef}>
                    {prizes.map((prize, index) =>
                        <PrizeCard
                            key={prize._id || index}
                            prize={prize}
                            showValues={config.showValues}
                            className={index === activeIndex ? "prizes-block__card--active" : ""}
                            onClick={() => prize.imageUrl && onPrizeClick?.({ url: prize.imageUrl, title: prize.title })}
                        />)}
                </div>

                {/* Right Arrow */}
                <button
                    type="button"
                    className="prizes-block__nav prizes-block__nav--right"
                    onClick={() => scrollCarousel("right")}
                    aria-label="Next prize"
                    disabled={activeIndex === prizes.length - 1}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            </div>

            {/* Pagination Dots */}
            {prizes.length > 1 &&
                <div className="prizes-block__dots">
                    {prizes.map((prize, index) =>
                        <button
                            key={prize._id || index}
                            type="button"
                            className={`prizes-block__dot ${index === activeIndex ? "prizes-block__dot--active" : ""}`}
                            onClick={() => {
                                setActiveIndex(index);
                                scrollToIndex(index);
                            }}
                            aria-label={`Go to prize ${index + 1}`}
                        />)}
                </div>
            }
        </div>
    );
};
